import { useState, useRef, useContext } from 'react'
import { useDisclosure, useToast } from "@chakra-ui/react";
import { useApolloClient } from "@apollo/client";
import { v4 as uuidv4 } from 'uuid';
import { PDFDocument } from 'pdf-lib';
import { PublisherAndUserContext } from '../../contexts/PublisherAndUserContext';
import { uploadWithSasTokenAndProgress, extractDetailsFromUrl } from '../../utils/helper';
import { useVideoUploader } from "../../hooks/useVideoUploader";
import { getCookie } from '../../utils/cookie';
import { useStore } from "../../Store";

export const useResourceFileUpload = ({type: pType, selectedFilesFromGallery, selectedNewFiles}) => {
    const { publisher } = useContext(PublisherAndUserContext);
    const { 
        updateStates, 
        bookSelectedFilesFromGallery, 
        courseSelectedFilesFromGallery,
    } = useStore((state) => state);
    const { uploadVideo } = useVideoUploader();
    const postUrl = process.env.REACT_APP_ADMIN_APP_GRAPHQL_ENDPOINT;

    const [isDragging, setIsDragging] = useState(false);
    const [fileType, setFileType] = useState(false);
    const [isGenerateOutline, setIsGenerateOutline] = useState(false);
    const STATUS = {
        UPLOADING: "uploading",
        PROCESSING: "processing",
        COMPLETED: "completed"
    }
    const fileInputRef = useRef(null);
    const videoIndexRef = useRef({});
    const uploadedFilesRef = useRef([]);
    const tempFilesRef = useRef([]);
    const toast = useToast();
    const apolloClient = useApolloClient();
    const {
        isOpen,
        onOpen,
        onClose
    } = useDisclosure();
    const fileTypes = {
        "image": "images",
        "video": "videos",
        "pdf": "pdfs"
    }
    
    const handleDragEnter = (e) => {
        e.preventDefault();
        setIsDragging(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        setIsDragging(false);
    };

    function renameFile(file, newName) {
        return new File([file], newName, {
          type: file.type,
          lastModified: file.lastModified,
        });
    }

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragging(false);
        const filesArray = Array.from(e.dataTransfer.files).map((f, index) => ({
            file: renameFile(e.dataTransfer.files[index], e.dataTransfer.files[index].name.replace(/ /g, '')),
            name: f.name.replace(/ /g, ''),
            size: f.size,
            type: fileTypes[f.type.split("/")[0]],
            lastModified: f.lastModified,
            includeInOutline: true,
            isNew: true,
            uuid: uuidv4(),
            fileType: f.type.includes("pdf") ? "pdf" : "image",
            url: ""
        }));
        updateStates({[`${pType}SelectedNewFiles`]: [...filesArray, ...selectedNewFiles]})
    };

    const handleFileInputChange = (e) => {
        const filesArray = Array.from(e.target.files).map((f, index) => ({
            file: renameFile(e.target.files[index], e.target.files[index].name.replace(/ /g, '')),
            name: f.name.replace(/ /g, ''),
            size: f.size,
            type: fileTypes[f.type.includes("pdf") ? f.type.split("/")[1] : f.type.split("/")[0]],
            lastModified: f.lastModified,
            includeInOutline: true,
            isNew: true,
            uuid: uuidv4(),
            fileType: f.type.includes("pdf") ? "pdf" : "image",
        }));
        updateStates({[`${pType}SelectedNewFiles`]: [...filesArray, ...selectedNewFiles]})
    };

    // const toggleIncludeInOutline = (rFile) => {
    //     updateStates({[`${pType}SelectedNewFiles`]: selectedNewFiles.map(file =>
    //         file.name === rFile.name ? { ...file, includeInOutline: !file.includeInOutline } : file
    //     )})
    //     updateStates({[`${pType}SelectedFilesFromGallery`]: selectedFilesFromGallery.map(file =>
    //         file.name === rFile.name ? { ...file, includeInOutline: !file.includeInOutline } : file
    //     )})
    // };

    const removeNewFile = (i) => {
        updateStates({[`${pType}SelectedNewFiles`]: selectedNewFiles.filter((f, index) => index !== i)})
        toast({
            position: "top-right",
            title: "File removed",
            status: "error",
            duration: 2000,
            isClosable: true
        });
    };
    const removeGalleryFile = (i) => {
        updateStates({[`${pType}SelectedFilesFromGallery`]: selectedFilesFromGallery.filter((f, index) => index !== i)})
        toast({
            position: "top-right",
            title: "File removed",
            status: "error",
            duration: 2000,
            isClosable: true
        });
    };

    const openResourceModal = (type) => {
        setFileType(type)
        onOpen()
    }

    const selectMedia = (type, link, resource) => { //Do not remove link
        updateStates({[`${pType}SelectedFilesFromGallery`]:
            selectedFilesFromGallery.some(f => f.url === resource.url)
                ? selectedFilesFromGallery.filter(f => f.url !== resource.url)
                : [{ ...resource, type, includeInOutline: true, isNew: false }, ...selectedFilesFromGallery]
        });
        tempFilesRef.current = [...tempFilesRef.current, resource]
    }

    const onSelectFiles = () => {
        tempFilesRef.current = []
        onClose()
    }

    const onCloseResourceModal = () => {
        const file = pType === "book" ? bookSelectedFilesFromGallery :courseSelectedFilesFromGallery
        updateStates({[`${pType}SelectedFilesFromGallery`]: file.filter(item1 => 
            !tempFilesRef.current.some(item2 => item1.url === item2.url)
          )
        });
        tempFilesRef.current = []
        onClose()
    }

    const uploadResourceFile = async (file, index) => {
        if(file.isNew) {
            try {
                let sasUrl = ""
                const store = useStore.getState()
                if(pType === "book") {
                    let bookUploadProgressCopy = store.bookUploadProgress
                    sasUrl = await uploadWithSasTokenAndProgress(publisher?.id, file.type, file.file, (progress) => {
                        bookUploadProgressCopy[index] = {progress, status: progress === 100 ? STATUS.COMPLETED : STATUS.UPLOADING}
                        updateStates({ bookUploadProgress: [...bookUploadProgressCopy] });
                    }, file.uuid)
                } else {
                    let courseUploadProgressCopy = store.courseUploadProgress
                    sasUrl = await uploadWithSasTokenAndProgress(publisher?.id, file.type, file.file, (progress) => {
                        courseUploadProgressCopy[index] = {progress, status: progress === 100 ? STATUS.COMPLETED : STATUS.UPLOADING}
                        updateStates({ courseUploadProgress: [...courseUploadProgressCopy] });
                    }, file.uuid)
                }
                if (!sasUrl) return alert("SAS URL is missing");
                const url = sasUrl.split("?")[0]
                const filesCopy = [...uploadedFilesRef.current]
                filesCopy[index] = {
                    ...filesCopy[index],
                    url: url,
                }
                uploadedFilesRef.current = [...filesCopy]
                const { id, folderName, fileName } = extractDetailsFromUrl(url)
                const newResource = {
                    key: `${id}/${folderName}/${fileName}`,
                    url: url,
                    type: file.type,
                    size: file.size,
                    name: file.name,
                };
                if (folderName === "images") {
                    apolloClient.cache.modify({
                        fields: {
                            edshopifyImageResources(existingEdshopifyImageResourcesRefs = {}, { readField }) {
                                const updatedResources = [
                                    newResource,
                                    ...existingEdshopifyImageResourcesRefs.resources,
                                ];
    
                                return {
                                    ...existingEdshopifyImageResourcesRefs,
                                    resources: updatedResources,
                                };
                            },
                        },
                    });
                }
                if (folderName === "audios") {
                    apolloClient.cache.modify({
                        fields: {
                            edshopifyAudioResources(existingEdshopifyAudioResourcesRefs = {}, { readField }) {
                                const updatedResources = [
                                    newResource,
                                    ...existingEdshopifyAudioResourcesRefs.resources,
                                ];
    
                                return {
                                    ...existingEdshopifyAudioResourcesRefs,
                                    resources: updatedResources,
                                };
                            },
                        },
                    });
                }
                if (folderName === "pdfs") {
                    apolloClient.cache.modify({
                        fields: {
                            edshopifyPdfResources(existingEdshopifyPdfResourcesRefs = {}, { readField }) {
                                const updatedResources = [
                                    newResource,
                                    ...existingEdshopifyPdfResourcesRefs.resources,
                                ];
    
                                return {
                                    ...existingEdshopifyPdfResourcesRefs,
                                    resources: updatedResources,
                                };
                            },
                        },
                    });
                }
            } catch (error) {
                console.error("Upload failed:", error);
                toast({ status: "error", title: `Error uploading ${file.name}` })
                toast({
                    position: "top-right",
                    title: `Error uploading ${file.name}`,
                    status: "error",
                    duration: 3000,
                    isClosable: true
                });
            }
        }
    };

    const uploadVideoFile = (data, index) => {
        if(data.isNew) {
            const store = useStore.getState()
            uploadVideo({title: data.name, videoFile: data.file}, (id, progress) => {
                if(pType === "book") {
                    let bookUploadProgressCopy = store.bookUploadProgress
                    bookUploadProgressCopy[index] = { progress, status: progress === 100 ? STATUS.PROCESSING : STATUS.UPLOADING }
                    updateStates({ bookUploadProgress: [...bookUploadProgressCopy] });
                } else {
                    let courseUploadProgressCopy = store.courseUploadProgress
                    courseUploadProgressCopy[index] = { progress, status: progress === 100 ? STATUS.PROCESSING : STATUS.UPLOADING }
                    updateStates({ courseUploadProgress: [...courseUploadProgressCopy] });
                }
                const filesCopy = [...uploadedFilesRef.current]
                filesCopy[index] = {
                    ...filesCopy[index],
                    uuid: id,
                    fileType: "video"
                }
                uploadedFilesRef.current = [...filesCopy]
                videoIndexRef.current = {
                    ...videoIndexRef.current,
                    [id]: index
                }
                if (progress === 100) {
                    polling(id, 30000)
                }
            }).catch((err) => {
                updateStates({[`${pType}IsUploading`]: false})
                toast({
                    position: "top-right",
                    description: err.message,
                    status: "error",
                    duration: 4000,
                    isClosable: true,
                });
                console.error("Upload errored:", err);
            })
        }
    }

    const polling = async (videoId, interval = 10000) => {
        return new Promise((resolve, reject) => {
            function checkStatus() {
                fetch(postUrl, {
                    method: "POST",
                    headers: {
                      "Content-Type": "application/json",
                      "x-access-token": getCookie("dyn_session"),
                    },
                    body: JSON.stringify({
                      query: `
                            query EdshopifyVideo($where: EdshopifyVideoWhereInput!) {
                                edshopifyVideo(where: $where) {
                                    id
                                    url
                                    name
                                    duration
                                    thumbnail
                                    description
                                    tags {
                                        tag
                                    }
                                    status
                                    uploadProgress
                              }
                            }
                          `,
                      variables: {
                        where: {
                            id: publisher?.id,
                            videoId: videoId
                        }
                      },
                    }),
                  }).then((res) => res.json())
                  .then((data) => {
                      const store = useStore.getState()
                      if(data.data.edshopifyVideo.status === "available") {
                        if(pType === "book") {
                            let bookUploadProgressCopy = store.bookUploadProgress
                            bookUploadProgressCopy[videoIndexRef.current[data.data.edshopifyVideo.id]] = {progress: 100, status: STATUS.COMPLETED}
                            updateStates({ bookUploadProgress: [...bookUploadProgressCopy] });
                        } else {
                            let courseUploadProgressCopy = store.courseUploadProgress
                            courseUploadProgressCopy[videoIndexRef.current[data.data.edshopifyVideo.id]] = {progress: 100, status: STATUS.COMPLETED}
                            updateStates({ courseUploadProgress: [...courseUploadProgressCopy] });
                        }
                        apolloClient.cache.modify({
                            fields: {
                                edshopifyVideos(existingedshopifyVideosRefs = {}, { readField }) {
                                    return {
                                        ...existingedshopifyVideosRefs,
                                        videos: existingedshopifyVideosRefs.videos.map((v) =>
                                            readField("id", v) === data.data.edshopifyVideo.id ? { ...v, ...data.data.edshopifyVideo } : v
                                        ),
                                    };
                                },
                            },
                        });
                        resolve()
                      } else {
                        setTimeout(() => checkStatus(), interval);
                      }
                  }).catch(reject)
            } 
            checkStatus()
        })
    }

    const uploadNewFiles = async () => {
        updateStates({ [`${pType}IsUploading`]: true });
        uploadedFilesRef.current = [...selectedNewFiles.filter(f => f.file instanceof File)];
    
        const validatePdf = async (file) => {
            try {
                const arrayBuffer = await file.file.arrayBuffer();
                const pdfDoc = await PDFDocument.load(arrayBuffer);
                const pageCount = pdfDoc.getPageCount();
    
                return { ...file, invalid: pageCount > 50 };
            } catch (error) {
                console.error("Error reading file:", file.file.name, error);
            }
        };
        const filesToUpload = await Promise.all(
            uploadedFilesRef.current.map((file) => {
                return file.fileType === "pdf" ? validatePdf(file) : Promise.resolve(file);
            })
        );
        updateStates({ [`${pType}SelectedNewFiles`]: filesToUpload });
    
        const hasInvalidFiles = filesToUpload.some(file => file.invalid);
    
        if (hasInvalidFiles) {
            updateStates({ [`${pType}IsUploading`]: false });
            toast({
                position: "top-right",
                description: `Failed to add. One or more PDFs exceed the 50-page limit.`,
                status: "error",
                duration: 6000,
                isClosable: true,
            });
            return;
        }
    
        await Promise.allSettled(
            uploadedFilesRef.current.map((file, index) =>
                file.type === "videos"
                    ? uploadVideoFile(file, index)
                    : uploadResourceFile(file, index)
            )
        );
    };
    

    return {
        handleDragEnter,
        handleDragLeave,
        handleDrop,
        handleFileInputChange,
        // toggleIncludeInOutline,
        removeNewFile,
        removeGalleryFile,
        isDragging,
        fileInputRef,
        // files,
        openResourceModal,
        isOpen,
        fileType,
        selectMedia,
        onClose,
        onSelectFiles,
        onCloseResourceModal,
        selectedFilesFromGallery,
        uploadNewFiles,
        uploadedFilesRef,
        isGenerateOutline, 
        setIsGenerateOutline
    }
}
